// A card gliding to a new place carries `data-moving` (on top of the other piles, its flight shadow,
// board.css) until its transform transition ends, or a fallback timer runs out. One glide per card at a
// time: a new glide (the deal right after the gather, a drop right after a release) cancels the previous
// one's listener and timer, else the old timer took `data-moving` off the card in the middle of its new
// flight (the dealt cards lost their flight order for a moment, caught by e2e card-motion MOTION-006).
const MOVE_FALLBACK_MS = 800; // the slowest glide (to the waste, board.css) is 400 ms
const running = new WeakMap<HTMLElement, () => void>();
/** The end of each running glide (endGlide). */
const ending = new WeakMap<HTMLElement, () => void>();

// The cards that start a glide in the same task (one commit: a run moved, undone, dropped) are a run per
// pile; its first (lowest) card is marked `data-run-head` and casts ONE shadow under the whole run
// (board.css). Per-card strip shadows overlapped at the joins and showed through the cards' transparent
// corners (owner 2026-10-16).
let pending: HTMLElement[] = [];

/** Marks the lowest card of each pile in `els` as its run's head, the others not. */
export function markRunHead(els: readonly HTMLElement[]): void {
  const heads = new Map<string, HTMLElement>();
  for (const el of els) {
    const pile = el.dataset.pile ?? '';
    const head = heads.get(pile);
    if (!head || Number(el.dataset.index) < Number(head.dataset.index)) heads.set(pile, el);
  }
  const chosen = new Set(heads.values());
  for (const el of els) {
    if (chosen.has(el)) el.dataset.runHead = '';
    else delete el.dataset.runHead;
  }
}

function queueRun(el: HTMLElement) {
  if (!pending.length) {
    queueMicrotask(() => {
      const batch = pending;
      pending = [];
      markRunHead(batch);
    });
  }
  pending.push(el);
}

/** `released`: the glide after a drag (its held shadow fades out on the way down, board.css). */
export function startGlide(el: HTMLElement, opts: { released?: boolean } = {}): void {
  running.get(el)?.();
  el.dataset.moving = '';
  queueRun(el);
  if (opts.released) el.dataset.released = '';
  const stop = () => {
    el.removeEventListener('transitionend', onEnd);
    clearTimeout(timer);
    running.delete(el);
    ending.delete(el);
  };
  const finish = () => {
    stop();
    delete el.dataset.moving;
    delete el.dataset.released;
    delete el.dataset.runHead;
    el.style.removeProperty('--classic-delay'); // a classic send-back's cascade (usePointerInput)
  };
  // only the card's own transform transition (not a picture's or the shadow layer's)
  const onEnd = (e: TransitionEvent) => {
    if (e.target === el && e.propertyName === 'transform' && !e.pseudoElement) finish();
  };
  const timer = setTimeout(finish, MOVE_FALLBACK_MS);
  el.addEventListener('transitionend', onEnd);
  running.set(el, stop);
  ending.set(el, finish);
  // A card already at its place (dropped right on it) gets no transform transition, so no transitionend:
  // without this check it stayed `data-moving` (pointer-events: none, unclickable) until the 800 ms
  // fallback (found by e2e FT-Q02: a double click right after a drop hit the card under it).
  // Chrome < 84 has no getAnimations() (2026-10-25 report, Chrome 81 on Android 4.4): there the transitionend or the
  // fallback ends it.
  requestAnimationFrame(() => {
    if (running.get(el) !== stop || typeof el.getAnimations !== 'function') return;
    const gliding = el.getAnimations().some((a) => (a as CSSTransition).transitionProperty === 'transform');
    if (!gliding) finish();
  });
}

/** Ends a card's glide at once: a press took the card where it lands (usePointerInput, player report 2026-10-25: a card
 *  still gliding could not be taken, the press took the card under it). Its transition jumps to the end. */
export function endGlide(el: HTMLElement): void {
  if (typeof el.getAnimations === 'function') {
    for (const a of el.getAnimations()) if ((a as CSSTransition).transitionProperty === 'transform') a.finish();
  }
  ending.get(el)?.();
}
